import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Pagination from "../../../component/library/pagination";
import {
  supplierInquiriesAdminList,
  supplierInquiriesAdminUpdate,
  supplierInquiriesAdminDelete,
  downloadSupplierInquiriesExcel,
} from "../../../service/admin/cantonFairLeadAdminApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import * as Opt from "../cantonFairLeads/formOptions";
import "../cantonFairLeads/style.css";

const STANDARD_STATUSES = ["new", "contacted", "in_progress", "closed"];

function goalsLabel(row) {
  if (!row?.goals?.length) return "—";
  return row.goals.join(", ");
}

function mapRowToForm(row) {
  return {
    companyName: row.companyName || "",
    contactName: row.contactName || "",
    wechatId: row.wechatId || "",
    whatsappPhone: row.whatsappPhone || "",
    email: row.email || "",
    productCategory: row.productCategory || "",
    monthlyExportVolume: row.monthlyExportVolume || "",
    targetMonthlyRevenue: row.targetMonthlyRevenue || "",
    goalsText: (row.goals || []).join("\n"),
    additionalNotes: row.additionalNotes || "",
    status: row.status || "new",
    adminNotes: row.adminNotes || "",
  };
}

function sanitizePhoneInput(raw) {
  let v = String(raw ?? "").replace(/[^\d+]/g, "");
  v = v.replace(/(?!^)\+/g, "");
  return v.slice(0, 20);
}

export default function SupplierInquiriesAdmin() {
  const { showToast } = useMessage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [q, setQ] = useState("");
  const [qDraft, setQDraft] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [dateFromDraft, setDateFromDraft] = useState("");
  const [dateToDraft, setDateToDraft] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const [viewRow, setViewRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (statusFilter) params.set("status", statusFilter);
    if (categoryFilter.trim()) params.set("productCategory", categoryFilter.trim());
    if (dateFrom.trim()) params.set("dateFrom", dateFrom.trim());
    if (dateTo.trim()) params.set("dateTo", dateTo.trim());
    const qs = `?${params.toString()}`;
    supplierInquiriesAdminList(qs)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setRows(res.data.results || []);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.total || 0,
          });
        } else {
          showToast(res?.status?.message || "Failed to load inquiries", ToastTypes.WARNING);
        }
      })
      .catch(() => showToast("Failed to load inquiries", ToastTypes.ERROR))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, q, statusFilter, categoryFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const applySearch = () => {
    setQ(qDraft);
    setDateFrom(dateFromDraft.trim());
    setDateTo(dateToDraft.trim());
    setPage(1);
  };

  const buildFilterQueryString = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (statusFilter) params.set("status", statusFilter);
    if (categoryFilter.trim()) params.set("productCategory", categoryFilter.trim());
    if (dateFrom.trim()) params.set("dateFrom", dateFrom.trim());
    if (dateTo.trim()) params.set("dateTo", dateTo.trim());
    const s = params.toString();
    return s ? `?${s}` : "";
  };

  const handleExportCsv = () => {
    setExporting(true);
    downloadSupplierInquiriesExcel(buildFilterQueryString())
      .then(() => showToast("Excel exported", ToastTypes.SUCCESS))
      .catch((err) => showToast(err?.message || "Export failed", ToastTypes.ERROR))
      .finally(() => setExporting(false));
  };

  const confirmDeleteInquiry = () => {
    if (!deleteTarget?._id) return;
    if (!String(deletePassword).trim()) {
      showToast("Enter your admin password to confirm deletion", ToastTypes.WARNING);
      return;
    }
    setDeleting(true);
    supplierInquiriesAdminDelete(deleteTarget._id, deletePassword)
      .then((res) => {
        if (res?.status?.code === 200) {
          showToast("Inquiry deleted", ToastTypes.SUCCESS);
          setDeleteTarget(null);
          setDeletePassword("");
          fetchList();
        } else {
          showToast(res?.status?.message || "Delete failed", ToastTypes.WARNING);
        }
      })
      .catch((err) =>
        showToast(err?.data?.status?.message || err?.message || "Delete failed", ToastTypes.ERROR)
      )
      .finally(() => setDeleting(false));
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeletePassword("");
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setEditForm(mapRowToForm(row));
  };

  const saveEdit = () => {
    if (!editForm || !editId) return;
    const goals = editForm.goalsText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      companyName: editForm.companyName,
      contactName: editForm.contactName,
      wechatId: editForm.wechatId,
      whatsappPhone: editForm.whatsappPhone,
      email: editForm.email,
      productCategory: editForm.productCategory,
      monthlyExportVolume: editForm.monthlyExportVolume,
      targetMonthlyRevenue: editForm.targetMonthlyRevenue,
      goals,
      additionalNotes: editForm.additionalNotes,
      status: editForm.status,
      adminNotes: editForm.adminNotes,
    };
    setSaving(true);
    supplierInquiriesAdminUpdate(editId, payload)
      .then((res) => {
        if (res?.status?.code === 200) {
          showToast("Saved", ToastTypes.SUCCESS);
          setEditForm(null);
          setEditId(null);
          fetchList();
        } else {
          showToast(res?.status?.message || res?.error?.message || "Save failed", ToastTypes.WARNING);
        }
      })
      .catch((err) => showToast(err?.message || "Save failed", ToastTypes.ERROR))
      .finally(() => setSaving(false));
  };

  return (
    <div className="cfl-page">
      <div className="cfl-header">
        <div>
          <h2 className="cfl-title">Exporterr supplier inquiries</h2>
          <p className="cfl-sub">Exporterr.com supplier form submissions (supplierinquiries)</p>
        </div>
        <div style={{ color: "#64748b", fontSize: 14 }}>
          Total: <strong>{pages.totalItems}</strong>
        </div>
      </div>

      <div className="cfl-toolbar">
        <input
          type="search"
          className="cfl-toolbar-search"
          placeholder="Search company, contact, email, category…"
          title="Company, contact, email, category"
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
        />
        <button type="button" className="cfl-toolbar-btn cfl-toolbar-btn-primary" onClick={applySearch}>
          Search
        </button>
        <select
          className="cfl-toolbar-select cfl-toolbar-purpose"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All inquiry status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="in_progress">In progress</option>
          <option value="closed">Closed</option>
        </select>
        <input
          type="text"
          className="cfl-toolbar-input cfl-toolbar-campaign"
          placeholder="Category contains"
          title="Filter by product category (partial match)"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(1);
          }}
        />
        <label className="cfl-date-lbl">
          <span className="cfl-date-lbl-text">From</span>
          <input type="date" value={dateFromDraft} onChange={(e) => setDateFromDraft(e.target.value)} className="cfl-date-input" />
        </label>
        <label className="cfl-date-lbl">
          <span className="cfl-date-lbl-text">To</span>
          <input type="date" value={dateToDraft} onChange={(e) => setDateToDraft(e.target.value)} className="cfl-date-input" />
        </label>
        <label className="cfl-rows-inline">
          <span className="cfl-rows-inline-text">Rows</span>
          <select
            className="cfl-toolbar-select cfl-toolbar-rows"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) || 20);
              setPage(1);
            }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="cfl-toolbar-btn cfl-toolbar-btn-secondary"
          onClick={() => {
            setQDraft("");
            setQ("");
            setStatusFilter("");
            setCategoryFilter("");
            setDateFromDraft("");
            setDateToDraft("");
            setDateFrom("");
            setDateTo("");
            setPage(1);
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="cfl-toolbar-btn cfl-toolbar-btn-export"
          onClick={handleExportCsv}
          disabled={exporting}
          title="Download Excel with current filters"
        >
          {exporting ? "Export…" : "Export Excel"}
        </button>
      </div>

      {loading ? (
        <div className="cfl-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="cfl-empty">No submissions found.</div>
      ) : (
        <>
          <div className="cfl-table-wrap">
            <table className="cfl-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Goals</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(pages.currentPage - 1) * pageSize + idx + 1}</td>
                    <td>{row.companyName || "—"}</td>
                    <td>{row.contactName || "—"}</td>
                    <td>{row.email || "—"}</td>
                    <td>{row.productCategory || "—"}</td>
                    <td className="cfl-purposes">{goalsLabel(row)}</td>
                    <td>{row.status || "—"}</td>
                    <td>{row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</td>
                    <td>
                      <div className="cfl-actions">
                        <button type="button" onClick={() => setViewRow(row)}>
                          View
                        </button>
                        <button type="button" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cfl-danger"
                          onClick={() => {
                            setDeletePassword("");
                            setDeleteTarget(row);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages.totalItems > 0 && (
            <div className="cfl-pagination-wrap">
              <Pagination pages={pages} page={page} setPage={setPage} />
            </div>
          )}
        </>
      )}

      {viewRow && (
        <div className="cfl-modal-overlay" role="dialog" aria-modal="true">
          <div className="cfl-modal">
            <div className="cfl-modal-header">
              <h3>Inquiry details</h3>
              <button type="button" className="cfl-modal-close" onClick={() => setViewRow(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <dl className="cfl-field-grid">
                <dt>Submitted</dt>
                <dd>{viewRow.createdAt ? moment(viewRow.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</dd>
                <dt>Updated</dt>
                <dd>{viewRow.updatedAt ? moment(viewRow.updatedAt).format("DD MMM YYYY, HH:mm") : "—"}</dd>
                <dt>Company</dt>
                <dd>{viewRow.companyName || "—"}</dd>
                <dt>Contact</dt>
                <dd>{viewRow.contactName || "—"}</dd>
                <dt>Email</dt>
                <dd>{viewRow.email || "—"}</dd>
                <dt>WhatsApp</dt>
                <dd>{viewRow.whatsappPhone || "—"}</dd>
                <dt>WeChat</dt>
                <dd>{viewRow.wechatId || "—"}</dd>
                <dt>Product category</dt>
                <dd>{viewRow.productCategory || "—"}</dd>
                <dt>Monthly export volume</dt>
                <dd>{viewRow.monthlyExportVolume || "—"}</dd>
                <dt>Target monthly revenue</dt>
                <dd>{viewRow.targetMonthlyRevenue || "—"}</dd>
                <dt>Goals</dt>
                <dd>{goalsLabel(viewRow)}</dd>
                <dt>Additional notes</dt>
                <dd style={{ whiteSpace: "pre-wrap" }}>{viewRow.additionalNotes || "—"}</dd>
                <dt>Status</dt>
                <dd>{viewRow.status || "—"}</dd>
                <dt>Admin notes</dt>
                <dd style={{ whiteSpace: "pre-wrap" }}>{viewRow.adminNotes || "—"}</dd>
                <dt>Record ID</dt>
                <dd>
                  <code style={{ fontSize: 12, wordBreak: "break-all" }}>{viewRow._id ? String(viewRow._id) : "—"}</code>
                </dd>
              </dl>
            </div>
            <div className="cfl-modal-footer">
              <button type="button" className="cfl-muted" onClick={() => setViewRow(null)}>
                Close
              </button>
              <button
                type="button"
                className="cfl-primary"
                onClick={() => {
                  openEdit(viewRow);
                  setViewRow(null);
                }}
              >
                Edit this inquiry
              </button>
            </div>
          </div>
        </div>
      )}

      {editForm && (
        <div className="cfl-modal-overlay" role="dialog" aria-modal="true">
          <div className="cfl-modal" style={{ maxWidth: 760 }}>
            <div className="cfl-modal-header">
              <h3>Edit inquiry</h3>
              <button type="button" className="cfl-modal-close" onClick={() => { setEditForm(null); setEditId(null); }} aria-label="Close">
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <div className="cfl-form-section">
                <h4>Contact</h4>
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Company name</span>
                    <input className="cfl-input" value={editForm.companyName} onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Contact name</span>
                    <input className="cfl-input" value={editForm.contactName} onChange={(e) => setEditForm({ ...editForm, contactName: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Email</span>
                    <input className="cfl-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>WhatsApp</span>
                    <input
                      className="cfl-input"
                      value={editForm.whatsappPhone}
                      onChange={(e) => setEditForm({ ...editForm, whatsappPhone: sanitizePhoneInput(e.target.value) })}
                    />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 12, color: "#64748b" }}>WeChat ID</span>
                    <input className="cfl-input" value={editForm.wechatId} onChange={(e) => setEditForm({ ...editForm, wechatId: e.target.value })} />
                  </label>
                </div>
              </div>

              <div className="cfl-form-section">
                <h4>Product &amp; volume</h4>
                <div style={{ display: "grid", gap: 10 }}>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Product category</span>
                    <select
                      className="cfl-input"
                      value={editForm.productCategory}
                      onChange={(e) => setEditForm({ ...editForm, productCategory: e.target.value })}
                    >
                      <option value="">—</option>
                      {editForm.productCategory && !Opt.PRODUCT_CATEGORIES.includes(editForm.productCategory) ? (
                        <option value={editForm.productCategory}>{editForm.productCategory}</option>
                      ) : null}
                      {Opt.PRODUCT_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Monthly export volume</span>
                    <input
                      className="cfl-input"
                      value={editForm.monthlyExportVolume}
                      onChange={(e) => setEditForm({ ...editForm, monthlyExportVolume: e.target.value })}
                    />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Target monthly revenue</span>
                    <input
                      className="cfl-input"
                      value={editForm.targetMonthlyRevenue}
                      onChange={(e) => setEditForm({ ...editForm, targetMonthlyRevenue: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <div className="cfl-form-section">
                <h4>Goals</h4>
                <label style={{ display: "block" }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>One goal per line</span>
                  <textarea className="cfl-input" rows={5} value={editForm.goalsText} onChange={(e) => setEditForm({ ...editForm, goalsText: e.target.value })} />
                </label>
              </div>

              <div className="cfl-form-section">
                <h4>Notes &amp; admin</h4>
                <div style={{ display: "grid", gap: 10 }}>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Additional notes</span>
                    <textarea className="cfl-input" rows={3} value={editForm.additionalNotes} onChange={(e) => setEditForm({ ...editForm, additionalNotes: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Status</span>
                    <select className="cfl-input" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                      {STANDARD_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                      {editForm.status && !STANDARD_STATUSES.includes(String(editForm.status).toLowerCase()) ? (
                        <option value={editForm.status}>{editForm.status}</option>
                      ) : null}
                    </select>
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Admin notes (internal)</span>
                    <textarea className="cfl-input" rows={3} value={editForm.adminNotes} onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })} />
                  </label>
                </div>
              </div>
            </div>
            <div className="cfl-modal-footer">
              <button type="button" className="cfl-muted" onClick={() => { setEditForm(null); setEditId(null); }} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="cfl-primary" onClick={saveEdit} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="cfl-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="si-delete-title"
          onClick={closeDeleteModal}
        >
          <div className="cfl-modal cfl-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cfl-modal-header">
              <h3 id="si-delete-title" className="cfl-delete-modal-title">
                Delete this inquiry?
              </h3>
              <button type="button" className="cfl-modal-close" onClick={closeDeleteModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <p className="cfl-delete-warn">
                This removes the submission from the database permanently. This action cannot be undone.
              </p>
              <div className="cfl-delete-summary">
                <div>
                  <span className="cfl-delete-k">Company</span>
                  <span className="cfl-delete-v">{deleteTarget.companyName || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Contact</span>
                  <span className="cfl-delete-v">{deleteTarget.contactName || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Email</span>
                  <span className="cfl-delete-v">{deleteTarget.email || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Submitted</span>
                  <span className="cfl-delete-v">
                    {deleteTarget.createdAt ? moment(deleteTarget.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
                  </span>
                </div>
                <div className="cfl-delete-id-row">
                  <span className="cfl-delete-k">Record ID</span>
                  <code className="cfl-delete-id">{String(deleteTarget._id)}</code>
                </div>
              </div>
              <label style={{ display: "block", marginTop: 16 }}>
                <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
                  Your admin password
                </span>
                <input
                  type="password"
                  className="cfl-input"
                  autoComplete="current-password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !deleting && confirmDeleteInquiry()}
                  placeholder="Enter password to confirm"
                  disabled={deleting}
                />
              </label>
            </div>
            <div className="cfl-modal-footer cfl-delete-footer">
              <button type="button" className="cfl-muted" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="cfl-btn-delete-confirm" onClick={confirmDeleteInquiry} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
